/**
 * Read-time repair for merged document states.
 *
 * Local edits are normalized at write time (inside the store's apply()), and
 * clients must never write normalization repairs in response to REMOTE updates
 * — two clients doing that would ping-pong repair writes at each other.
 * Instead, whenever a snapshot comes out of the doc from a non-local origin
 * (remote peer, undo manager, applied AI proposal), it is repaired here at
 * read time: every client runs the same pure function over the same converged
 * doc state, so every client renders the same repaired view. The repairs get
 * written back naturally by whichever client makes the next local edit.
 */
import { normalizeAllTracks } from '../format/invariants';
import type { Edit, VideoClip } from '../format/types';

export function deriveRenderEdit(edit: Edit): Edit {
  const out = structuredClone(edit);
  normalizeAllTracks(out.tracks);
  return out;
}

/**
 * Timeline seconds at which read-time repair ADDED a transition that the raw
 * merged state did not carry. Overlap encodes a transition, so two peers'
 * independently-fine edits can merge into an overlap and normalization then
 * manufactures a crossfade nobody authored (docs/realtime-collab.md). The
 * semantics are by design — but they should be *noticed*, not silent.
 */
export function manufacturedTransitions(raw: Edit, derived: Edit): number[] {
  if (raw === derived) return [];
  const times: number[] = [];
  for (let i = 0; i < derived.tracks.length; i++) {
    const dt = derived.tracks[i];
    const rt = raw.tracks[i];
    if (!rt || dt === rt || dt.kind !== 'video' || rt.kind !== 'video') continue;
    const rawById = new Map(rt.clips.map((c) => [c.id, c]));
    for (const clip of dt.clips) {
      const before = rawById.get(clip.id) as VideoClip | undefined;
      if (clip.transitionIn && before && !before.transitionIn) times.push(clip.start);
    }
  }
  return times;
}
